/**
 * View Transitions Manager
 * ============================================================================
 * Manages cross-document view transitions for smooth page navigation.
 *
 * This module:
 * 1. Detects navigation patterns to apply transition types
 * 2. Tracks clicked entry cards for shared element transitions
 * 3. Works with the Navigation API for transition type classification
 *
 * Browser Support:
 * - Chrome 126+: Full support
 * - Edge 126+: Full support
 * - Safari 18.2+: Full support
 * - Firefox: Graceful fallback (instant navigation)
 */

/**
 * Store the last clicked entry ID for shared element matching.
 * Used to coordinate view-transition-name between list and detail pages.
 */
let lastClickedEntryId = null;

/**
 * Settings pages tab order for determining forward/back navigation
 */
const SETTINGS_TAB_ORDER = [
  'profile',
  'account',
  'notifications',
  'passkeys',
  'security',
];

/**
 * How-to-play pages sport order for determining next/prev navigation
 */
const GUIDE_SPORT_ORDER = ['nfl', 'nba', 'mlb', 'nhl'];

/**
 * Sponsor room tab order for determining forward/back lateral navigation.
 * Mirrors the sidebar nav order in sidebar-sponsor-room.blade.php.
 */
export const SPONSOR_ROOM_TAB_ORDER = [
  'show',
  'analytics',
  'branding',
  'profile',
  'content',
  'prizes',
  'members',
  'settings',
  'invites',
];

/**
 * Initialize view transitions functionality.
 * Should be called once when the app loads.
 */
export function initViewTransitions() {
  // Feature detection - bail early if not supported
  if (!supportsViewTransitions()) {
    console.log(
      '[ViewTransitions] Not supported in this browser, using instant navigation'
    );
    return;
  }

  // Track entry card clicks for shared element transitions
  setupEntryCardTracking();

  // Set up transition type detection on page reveal
  setupTransitionTypeDetection();

  // Apply stored entry ID on page load (for shared element matching)
  applySharedElementName();

  console.log('[ViewTransitions] Initialized successfully');
}

/**
 * Check if the browser supports cross-document view transitions.
 * @returns {boolean}
 */
export function supportsViewTransitions() {
  // Check for View Transitions API
  if (!('ViewTransition' in window)) {
    return false;
  }

  // Check for Navigation API (needed for transition types)
  if (!('navigation' in window)) {
    return false;
  }

  return true;
}

/**
 * Set up click tracking on entry cards for shared element transitions.
 * When a user clicks an entry card, we store its ID so the detail page
 * can match the view-transition-name.
 */
function setupEntryCardTracking() {
  document.addEventListener('click', (event) => {
    const entryCard = event.target.closest('.entry-card[data-entry-id]');
    if (entryCard) {
      lastClickedEntryId = entryCard.dataset.entryId;
      // Store in sessionStorage for cross-document access
      try {
        sessionStorage.setItem('vt-entry-id', lastClickedEntryId);
      } catch {}
    }

    // Also track room cards
    const roomCard = event.target.closest('.room-card[data-room-id]');
    if (roomCard) {
      try {
        sessionStorage.setItem('vt-room-id', roomCard.dataset.roomId);
      } catch {}
    }
  });
}

/**
 * Set up transition type detection based on navigation patterns.
 * Uses the Navigation API's pagereveal event to classify transitions.
 */
function setupTransitionTypeDetection() {
  window.addEventListener('pagereveal', (event) => {
    const transition = event.viewTransition;
    if (!transition) return;

    // Get navigation activation info
    const activation = window.navigation?.activation;
    if (!activation) return;

    const fromUrl = activation.from?.url;
    const toUrl = activation.entry?.url;

    if (fromUrl && toUrl) {
      const types = determineTransitionTypes(fromUrl, toUrl);
      try {
        types.forEach((type) => {
          transition.types.add(type);
        });
      } catch {
        // Transition was aborted before types could be applied (e.g., rapid navigation)
        return;
      }

      if (types.length > 0) {
        console.log('[ViewTransitions] Applied types:', types);
      }
    }
  });
}

/**
 * Apply the stored entry ID as a view-transition-name to the detail page hero.
 * This creates the shared element animation between card and detail.
 */
function applySharedElementName() {
  let storedEntryId = null;
  try {
    storedEntryId = sessionStorage.getItem('vt-entry-id');
  } catch {}
  const detailHero = document.querySelector('.entry-detail-hero');

  if (storedEntryId && detailHero) {
    detailHero.style.viewTransitionName = `entry-${storedEntryId}`;
    // Clear after applying (one-time use)
    try {
      sessionStorage.removeItem('vt-entry-id');
    } catch {}
  }

  // Also handle room detail pages
  let storedRoomId = null;
  try {
    storedRoomId = sessionStorage.getItem('vt-room-id');
  } catch {}
  const roomHero = document.querySelector('.room-detail-hero');

  if (storedRoomId && roomHero) {
    roomHero.style.viewTransitionName = `room-${storedRoomId}`;
    try {
      sessionStorage.removeItem('vt-room-id');
    } catch {}
  }
}

/**
 * Determine which transition types to apply based on navigation URLs.
 * @param {string} fromUrl - The URL navigating from
 * @param {string} toUrl - The URL navigating to
 * @returns {string[]} Array of transition type strings
 */
export function determineTransitionTypes(fromUrl, toUrl) {
  const from = new URL(fromUrl);
  const to = new URL(toUrl);
  const types = [];

  // === Dashboard transitions ===
  if (to.pathname === '/dashboard' || to.pathname === '/dashboard/') {
    types.push('to-dashboard');
  }
  // Leaving dashboard
  if (from.pathname === '/dashboard' || from.pathname === '/dashboard/') {
    types.push('leave-dashboard');
  }

  // === Entry hierarchy ===
  // List -> Detail
  if (from.pathname === '/entries' && to.pathname.match(/^\/entries\/[^/]+$/)) {
    types.push('entry-forward');
  }
  // Detail -> List (back)
  if (from.pathname.match(/^\/entries\/[^/]+$/) && to.pathname === '/entries') {
    types.push('entry-back');
  }
  // Detail -> Board Edit (/entries/board/{uuid})
  if (
    from.pathname.match(/^\/entries\/[^/]+$/) &&
    to.pathname.match(/^\/entries\/board\/[^/]+$/)
  ) {
    types.push('entry-forward');
  }
  // Board Edit -> Detail (back)
  if (
    from.pathname.match(/^\/entries\/board\/[^/]+$/) &&
    to.pathname.match(/^\/entries\/[^/]+$/)
  ) {
    types.push('entry-back');
  }

  // === Room transitions ===
  // Room browse → Room detail
  if (to.pathname.match(/^\/rooms\/[^/]+$/)) {
    types.push('room-enter');
  }
  // Room detail → back (but not to a sub-page like /rooms/slug/leaderboard)
  if (
    from.pathname.match(/^\/rooms\/[^/]+$/) &&
    !to.pathname.match(/^\/rooms\/[^/]+\//)
  ) {
    types.push('room-exit');
  }

  // === Settings lateral navigation ===
  if (
    from.pathname.includes('/settings') &&
    to.pathname.includes('/settings')
  ) {
    const fromTab = SETTINGS_TAB_ORDER.findIndex((t) =>
      from.pathname.includes(t)
    );
    const toTab = SETTINGS_TAB_ORDER.findIndex((t) => to.pathname.includes(t));

    if (fromTab !== -1 && toTab !== -1 && fromTab !== toTab) {
      types.push(toTab > fromTab ? 'settings-forward' : 'settings-back');
    }
  }

  // === Auth transitions ===
  if (
    to.pathname === '/login' ||
    to.pathname === '/register' ||
    to.pathname === '/sponsor/login' ||
    to.pathname === '/sponsor/register'
  ) {
    types.push('auth-enter');
  }
  // After successful auth
  if (
    (from.pathname === '/login' || from.pathname === '/register') &&
    to.pathname === '/dashboard'
  ) {
    types.push('auth-exit');
  }

  // === Sponsor portal transitions ===
  // Arriving at sponsor dashboard
  if (
    to.pathname === '/sponsor/dashboard' ||
    to.pathname === '/sponsor/dashboard/'
  ) {
    types.push('to-sponsor-dashboard');
  }
  // Leaving sponsor dashboard
  if (
    from.pathname === '/sponsor/dashboard' ||
    from.pathname === '/sponsor/dashboard/'
  ) {
    types.push('leave-sponsor-dashboard');
  }

  // Sponsor room tab lateral navigation (same room, different tabs)
  const sponsorRoomTabPattern = /^\/sponsor\/rooms\/([^/]+)\/([^/]+)$/;
  const sponsorRoomShowPattern = /^\/sponsor\/rooms\/([^/]+)$/;
  const fromSponsorTab = from.pathname.match(sponsorRoomTabPattern);
  const toSponsorTab = to.pathname.match(sponsorRoomTabPattern);
  const fromSponsorShow = from.pathname.match(sponsorRoomShowPattern);
  const toSponsorShow = to.pathname.match(sponsorRoomShowPattern);

  // Normalize: /sponsor/rooms/:id is the "show" tab
  const fromRoomId = fromSponsorTab?.[1] ?? fromSponsorShow?.[1] ?? null;
  const toRoomId = toSponsorTab?.[1] ?? toSponsorShow?.[1] ?? null;
  const fromTab = fromSponsorTab?.[2] ?? (fromSponsorShow ? 'show' : null);
  const toTab = toSponsorTab?.[2] ?? (toSponsorShow ? 'show' : null);

  if (
    fromRoomId &&
    toRoomId &&
    fromRoomId === toRoomId &&
    fromTab &&
    toTab &&
    fromTab !== toTab
  ) {
    const fromIdx = SPONSOR_ROOM_TAB_ORDER.indexOf(fromTab);
    const toIdx = SPONSOR_ROOM_TAB_ORDER.indexOf(toTab);
    if (fromIdx !== -1 && toIdx !== -1) {
      types.push(toIdx > fromIdx ? 'sponsor-tab-forward' : 'sponsor-tab-back');
    }
  }

  // === Marketing pages ===
  const marketingPaths = [
    '/',
    '/features',
    '/sponsors',
    '/faq',
    '/calendar',
    '/pricing',
  ];
  if (marketingPaths.includes(to.pathname)) {
    types.push('marketing');
  }

  // === How-to-play guides ===
  if (to.pathname.includes('/how-to-play')) {
    const fromSport = GUIDE_SPORT_ORDER.findIndex((s) =>
      from.pathname.includes(s)
    );
    const toSport = GUIDE_SPORT_ORDER.findIndex((s) => to.pathname.includes(s));

    if (fromSport !== -1 && toSport !== -1 && fromSport !== toSport) {
      types.push(toSport > fromSport ? 'guide-next' : 'guide-prev');
    }
  }

  return types;
}

/**
 * Manually trigger a view transition for a same-page update.
 * Useful for tab changes or modal opens.
 * @param {Function} updateCallback - Function that updates the DOM
 * @returns {Promise<void>}
 */
export async function triggerViewTransition(updateCallback) {
  if (!document.startViewTransition) {
    // Fallback: just run the update
    updateCallback();
    return;
  }

  const transition = document.startViewTransition(updateCallback);
  try {
    await transition.finished;
  } catch {
    // Transition was aborted (e.g., another transition started)
  }
}

/**
 * Set a custom view-transition-name on an element.
 * @param {HTMLElement} element - The element to name
 * @param {string} name - The transition name
 */
export function setTransitionName(element, name) {
  if (element) {
    element.style.viewTransitionName = name;
  }
}

/**
 * Clear a view-transition-name from an element.
 * @param {HTMLElement} element - The element to clear
 */
export function clearTransitionName(element) {
  if (element) {
    element.style.viewTransitionName = '';
  }
}
